import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import { useTheme } from '@emotion/react'
import {
  Button,
  Section,
  SectionTitle,
  Container,
  Inline,
  ListItem,
} from 'chaoskit/src/components'
import { MDXRenderer } from 'gatsby-plugin-mdx'

import {
  HomeHero,
  HomeHeroGrid,
  HomeHeroGridImage,
  HomeHeroGridContent,
} from '~partials/home/Hero'
import BaseLayout from '~layouts/BaseLayout'
import CustomerQuote from '~partials/home/CustomerQuote'
import HelmetSEO from '~partials/global/HelmetSEO'
import { HeroTitleSub, HeroTitle, HeroContent } from '~partials/Hero'
import { ReactComponent as HeroSVG } from '~media/kubernetes/hero.svg'
import Data from '~components/Data'
import CustomerLogos from '~partials/global/CustomerLogos'
import KubernetesOverview from '~partials/product/KubernetesOverview'
import MarketoForm from '~components/MarketoForm'
import ButtonLink from '~components/ButtonLink'
import track from '~helpers/track'

const Kubernetes = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()

  return (
    <BaseLayout footerProps={{ hideCTA: true }}>
      <HelmetSEO title={pageData.title} description={pageData.description} />
      <HomeHero>
        <HeroContent>
          <HomeHeroGrid>
            <HomeHeroGridImage size="large">
              <HeroSVG />
            </HomeHeroGridImage>
            <HomeHeroGridContent>
              <HeroTitle size="large">
                <Data>{pageData.hero.heading}</Data>
              </HeroTitle>
              <HeroTitleSub>
                <Data>{pageData.hero.blurb}</Data>
              </HeroTitleSub>
              <div css={{ marginTop: theme.space.large }}>
                <Inline
                  css={{
                    justifyContent: 'center',

                    [theme.mq.medium]: {
                      justifyContent: 'flex-start',
                    },
                  }}
                >
                  <ListItem>
                    <Button url="#form" noContrast type="primary">
                      <Data>{pageData.hero.cta.label}</Data>
                    </Button>
                  </ListItem>
                  <ListItem>
                    <ButtonLink
                      type="default"
                      url="/gremlin-free-software?ref=kubernetes"
                    >
                      Get started free
                    </ButtonLink>
                  </ListItem>
                </Inline>
              </div>
            </HomeHeroGridContent>
          </HomeHeroGrid>
          <CustomerLogos
            css={{ paddingBottom: `${theme.space.base}px !important` }}
            contrast
          />
        </HeroContent>
      </HomeHero>
      <Container>
        <KubernetesOverview />
      </Container>

      <CustomerQuote />
      <Section id="form">
        <Container>
          <SectionTitle
            as="h2"
            title={<Data>{pageData.footer.heading}</Data>}
            sub={<Data>{pageData.footer.blurb}</Data>}
          />
          <div
            css={{
              display: 'grid',
              justifyContent: 'center',

              [theme.mq.medium]: {
                gridTemplateColumns: 'minmax(auto, 0.5fr)',
              },
            }}
          >
            <div>
              <MarketoForm
                id={pageData.footer.form.embed}
                fieldValues={{ programID: pageData.footer.form.programId }}
                thankYouMessage={pageData.footer.form.thankYouMessage}
                onSuccess={() => {
                  track('Form submitted', {
                    label: `marketo-${pageData.footer.form.embed}`,
                  })
                }}
              />
            </div>
          </div>
          <div
            css={{
              marginTop: theme.space.base,
              textAlign: 'center',
              ...theme.text.small,
            }}
          >
            <MDXRenderer>{pageData.footer.belowForm}</MDXRenderer>
          </div>
        </Container>
      </Section>
    </BaseLayout>
  )
}

Kubernetes.propTypes = {
  data: PropTypes.object.isRequired,
}

export default Kubernetes

export const pageQuery = graphql`
  query KubernetesPageData {
    file(name: { eq: "kubernetes" }, absolutePath: { regex: "/src/data/" }) {
      childMdx {
        frontmatter {
          title
          description
          hero {
            heading
            blurb
            cta {
              label
            }
          }
          footer {
            heading
            blurb
            form {
              heading
              embed
              programId
              thankYouMessage
            }
            belowForm
          }
        }
      }
    }
  }
`
